import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  Typography,
  Button,
  CircularProgress,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { makeStyles } from '@material-ui/core/styles';
import { appUrl } from '../../../../../../utils/config';

const getApiBase = () =>
  `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}`;

const toDateStr = (dateVal) => {
  if (!dateVal) return null;
  if (typeof dateVal === 'string' && dateVal.includes('T')) return dateVal.split('T')[0];
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const useStyles = makeStyles(() => ({
  paper: {
    borderRadius: 16,
    padding: '40px 36px 32px',
    textAlign: 'center',
    minWidth: 340,
    maxWidth: 420,
    width: '100%',
  },
  successIcon: {
    color: '#2EAA2E',
    fontSize: 72,
    display: 'block',
    margin: '0 auto 20px',
  },
  title: {
    fontWeight: 700,
    fontSize: '1.2rem',
    marginBottom: 28,
    lineHeight: 1.5,
    color: '#111',
  },
  tableWrap: {
    maxHeight: 260,
    overflowY: 'auto',
    border: '1px solid #E0E0E0',
    borderRadius: 4,
    marginBottom: 24,
    textAlign: 'left',
  },
  headCell: {
    fontWeight: 700,
    fontSize: '0.78rem',
    padding: '7px 10px',
    backgroundColor: '#F9F9FC',
    borderBottom: '1px solid #E0E0E0',
    whiteSpace: 'nowrap',
    lineHeight: 1.3,
  },
  cell: {
    fontSize: '0.8rem',
    padding: '6px 10px',
    borderBottom: '1px solid #F0F0F0',
    lineHeight: 1.4,
  },
  errorText: {
    color: '#A02828',
    fontSize: '0.85rem',
    marginBottom: 16,
  },
  okBtn: {
    textTransform: 'none',
    borderRadius: 24,
    backgroundColor: '#E75B25',
    color: '#fff',
    padding: '8px 52px',
    fontSize: '0.95rem',
    fontWeight: 600,
    '&:hover': { backgroundColor: '#C44A1A' },
  },
}));

const GenerateConsigneeGRNDialog = ({ open, row, onClose }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open) {
      setLoading(true);
      setError(false);
      return;
    }
    generateGRN();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row]);

  const generateGRN = async () => {
    setLoading(true);
    setError(false);
    try {
      const today = toDateStr(new Date());

      const payload = {
        sponsorShipmentID: row?.sponsorShipmentID || null,
        consigneeGRNMetadata: {
          plant:         row?.vendorPlant         || null,
          companyCode:   row?.companyCode   || null,
          purchaseOrg:   row?.purchasingOrg?.[0]?.purchasingOrgCode     || '',
          purchaseGroup: row?.purchasingGroup?.[0]?.purchasingGroupCode || '',
          goodsIssueDate:   today,
          goodsReceiptDate: today,
          items: (row?.batchesList || []).map((b) => ({
            schrockenDeliveryScheduleBatchID: b.schrockenDeliveryScheduleBatchID,
            fgMaterialCode:   (b.fgMaterialCode || '').padStart(18, '0'),
            batchQuantity:    String(b.dispatchQuantity ?? ''),
            batchQuantityUOM: b.dispatchUOM || '',
            deliveryDate:     today,
            storageLocation:  b.storageLocation   || 'PCDS',
            fgBatch:          b.vendorBatchNumber || '',
          })),
        },
      };

      const res = await axios.post(`${getApiBase()}/generateConsigneeGRN`, payload);
      const data = res.data;

      if (data.success) {
        // success — no data to store
      } else {
        setError(true);
      }
    } catch (err) {
      console.error('[GenerateConsigneeGRN] API failed', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && open && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.45)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          gap: 16,
        }}>
          <CircularProgress size={52} style={{ color: '#fff' }} />
          <Typography style={{ color: '#fff', fontSize: '1rem', fontWeight: 500 }}>
            Generating Consignee GRN…
          </Typography>
        </div>
      )}
      <Dialog open={open && !loading} classes={{ paper: classes.paper }}>
        {error ? (
          <>
            <Typography className={classes.errorText}>
              Something went wrong. Please try again.
            </Typography>
            <Button className={classes.okBtn} onClick={onClose}>
              Close
            </Button>
          </>
        ) : (
          <>
            <CheckCircleIcon className={classes.successIcon} />
            <Typography className={classes.title}>
              Consignee GRN Generated,<br />notified Sponsor SAP
            </Typography>
            <Button className={classes.okBtn} onClick={onClose}>
              Ok
            </Button>
          </>
        )}
      </Dialog>
    </>
  );
};

export default GenerateConsigneeGRNDialog;
